import "./pages/DealSummary/DealSummary.css";

import { useCallback, useEffect, useMemo, useState } from "react";
import { useSearchParams } from "react-router-dom";

import { CalendarDarkIcon, PercentIcon, PoundIcon, UptrendArrowGreenIcon } from "../../../assets";
import { InteractionObjectType, LocalStorageConstants } from "../../../constants";
import { EditDealDatesPanel, NotesPanel, TagsPanel } from "../../../templates";
import { useAuth } from "../../../useAuth";
import {
  convertDateToStringDDMMYYYY,
  formatCurrency,
  formatNumber,
  formatPercent,
  stringKeyToSentenceCase,
} from "../../../utils";
import { InformationBox, SeedFooter, SeedNavigation, Toast } from "../../../widget";
import { SelectWorkflowTemplateModal } from "../../shared/components/SelectWorkflowTemplateModal";
import { parseDealStages } from "../../shared/workflows/workflow-templates/tasks-board/utils";
import {
  CloseDealModal,
  DEAL_KPI_OPTIONS,
  DealKpiKey,
  DealSummaryHeaderSection,
  DealSummaryHeaderSectionProps,
  DealSummaryKpiCard,
  DEFAULT_SELECTED_DEAL_KPI_KEYS,
  getStoredDealKpiKeys,
  saveDealKpiKeys,
  SelectDealKpisPanel,
  UploadDealDocumentModal,
} from "./components";
import { DEAL_SLUG_BY_TAB, DealTab, DEFAULT_DEAL_TAB_SLUG, getDealTabFromSlug } from "./dealTabs";
import DealStructure from "./pages/DealStructure/DealStructure";
import DealSummary from "./pages/DealSummary/DealSummary";
import DealDiligence from "./pages/DueDiligence/DealDiligence";
import { useDeal } from "./useDeal";

const Deal = (): JSX.Element => {
  const [searchParams, setSearchParams] = useSearchParams();
  const { user } = useAuth();
  const {
    deal,
    canEdit,
    canUploadDealDocument,
    dealWorkflowUuid,
    workflowTemplatePhases,
    isLoading,
    isEditing,
    isSaving,
    validationErrors,
    dealSummaryForm,
    dealSourceOptions,
    organisationUsersSelectData,
    quickActionsMenuOptionsList,
    showQuickActionsMenu,
    setShowQuickActionsMenu,
    showSelectWorkflowTemplateModal,
    setShowSelectWorkflowTemplateModal,
    showEditDealDatesPanel,
    setShowEditDealDatesPanel,
    showCloseDealModal,
    closeCloseDealModal,
    applyCloseDealUpdate,
    refreshDeal,
    notePreviewItems,
    refreshNotes,
    tags,
    showTagsPanel,
    setShowTagsPanel,
    refreshTags,
    startEditing,
    cancelEditing,
    saveDealSummary,
    setDealSummaryFormValue,
    handleBackClick,
    dealStructureColumns,
    dealStructureData,
    onChangeDealStructure,
    dealDocumentsColumns,
    dealDocumentsLoading,
    onDealDocGridChange,
    dealDocumentsRefreshToken,
    setDealDocumentsRefreshToken,
    dealUuid,
    showUploadDealDocumentModal,
    uploadDealDocumentModalDocumentType,
    uploadDealDocumentModalCustomVariant,
    uploadDealDocumentModalDealDocumentUuid,
    openUploadDealDocumentModal,
    closeUploadDealDocumentModal,
  } = useDeal();
  // The active tab is driven by the ?tab= query param so it's shareable/bookmarkable, while only
  // location.search changes on switch (pathname stays put) — no remount, no data refetch.
  const activeTab = getDealTabFromSlug(searchParams.get("tab") ?? undefined);
  const goToTab = useCallback(
    (tab: DealTab): void => {
      const next = new URLSearchParams(searchParams);
      const slug = DEAL_SLUG_BY_TAB[tab];
      if (slug === DEFAULT_DEAL_TAB_SLUG) next.delete("tab");
      else next.set("tab", slug);
      setSearchParams(next, { replace: true });
    },
    [searchParams, setSearchParams]
  );
  const [showNotesPanel, setShowNotesPanel] = useState<boolean>(false);
  const [showSelectDealKpisPanel, setShowSelectDealKpisPanel] = useState<boolean>(false);
  const [selectedKpiKeys, setSelectedKpiKeys] = useState<DealKpiKey[]>(DEFAULT_SELECTED_DEAL_KPI_KEYS);

  const pipelineTypeLabel = deal?.pipeline?.type;
  const dealKpiLocalStorageKey = useMemo(
    () => `${user?.userUuid ? `${user.userUuid}_` : ""}${LocalStorageConstants.DEAL_LISTING_KPI_PREFERENCES}`,
    [user?.userUuid]
  );
  const stageItems = useMemo(
    () => parseDealStages(deal?.dealDates, workflowTemplatePhases),
    [deal?.dealDates, workflowTemplatePhases]
  );

  useEffect(() => {
    setSelectedKpiKeys(getStoredDealKpiKeys(dealKpiLocalStorageKey));
  }, [dealKpiLocalStorageKey]);

  const getDealKpiCard = useCallback(
    (kpiKey: DealKpiKey): DealSummaryKpiCard | undefined => {
      const option = DEAL_KPI_OPTIONS.find((dealKpiOption) => dealKpiOption.key === kpiKey);
      if (!option) return undefined;

      switch (kpiKey) {
        case "targetCloseDate":
          return {
            key: kpiKey,
            label: option.label,
            value: deal?.targetCloseDate ? convertDateToStringDDMMYYYY(deal.targetCloseDate) : "--",
            icon: <CalendarDarkIcon className="KPICardIcon" />,
          };
        case "introductionDate":
          return {
            key: kpiKey,
            label: option.label,
            value: deal?.introductionDate ? convertDateToStringDDMMYYYY(deal.introductionDate) : "--",
            icon: <CalendarDarkIcon className="KPICardIcon" />,
          };
        case "createdAt":
          return {
            key: kpiKey,
            label: option.label,
            value: deal?.createdAt ? convertDateToStringDDMMYYYY(deal.createdAt) : "--",
            icon: <CalendarDarkIcon className="KPICardIcon" />,
          };
        case "investmentValue":
          return {
            key: kpiKey,
            label: option.label,
            value: formatCurrency(deal?.investmentValue),
            icon: <PoundIcon className="KPICardIcon" />,
          };
        case "targetReturns":
          return {
            key: kpiKey,
            label: option.label,
            value: formatCurrency(deal?.targetReturns),
            icon: <PoundIcon className="KPICardIcon" />,
          };
        case "expectedFees":
          return {
            key: kpiKey,
            label: option.label,
            value: formatCurrency(deal?.estimatedFees),
            icon: <PoundIcon className="KPICardIcon" />,
          };
        case "moneyMultiple":
          return {
            key: kpiKey,
            label: option.label,
            value:
              deal?.moneyMultiple !== null && deal?.moneyMultiple !== undefined
                ? formatNumber(deal.moneyMultiple) || "--"
                : "--",
            icon: <UptrendArrowGreenIcon className="KPICardIcon" />,
          };
        case "projectIrr":
          return {
            key: kpiKey,
            label: option.label,
            value: formatPercent(deal?.projectIrr, "ratio", 1),
            icon: <UptrendArrowGreenIcon className="KPICardIcon" />,
          };
        case "conversionProbability":
          return {
            key: kpiKey,
            label: option.label,
            value: formatPercent(deal?.conversionProbability, "ratio", 1),
            icon: <PercentIcon className="KPICardIcon" />,
          };
        default:
          return undefined;
      }
    },
    [deal]
  );

  const kpiCards = useMemo<DealSummaryKpiCard[]>(
    () =>
      selectedKpiKeys
        .map((kpiKey) => getDealKpiCard(kpiKey))
        .filter((kpiCard): kpiCard is DealSummaryKpiCard => kpiCard !== undefined),
    [getDealKpiCard, selectedKpiKeys]
  );

  const handleSaveDealKpis = useCallback(
    (nextSelectedKpiKeys: DealKpiKey[]): void => {
      try {
        saveDealKpiKeys(dealKpiLocalStorageKey, nextSelectedKpiKeys);
        setSelectedKpiKeys(nextSelectedKpiKeys);
        setShowSelectDealKpisPanel(false);
      } catch (err) {
        const message = err instanceof Error ? err.message : "An unknown error occurred while saving KPI preferences.";
        Toast.warn({ message });
      }
    },
    [dealKpiLocalStorageKey]
  );

  const dealQuickActionsMenuOptionsList = useMemo(
    () => [
      ...quickActionsMenuOptionsList,
      {
        id: 1000,
        value: "Select KPIs",
        action: () => setShowSelectDealKpisPanel(true),
      },
    ],
    [quickActionsMenuOptionsList]
  );

  const teamMembers = useMemo(() => {
    const list =
      deal?.usersInvolved?.map((member) => ({
        key: `${member.email}-${member.role}`,
        name: member.fullName ?? member.email,
        role: member.role,
        email: member.email,
        avatarUrl: undefined,
      })) ?? [];

    if (deal?.leadUser?.fullName || deal?.leadUser?.email) {
      const leadKey = deal?.leadUser?.email ?? deal?.leadUser?.fullName ?? "lead";
      if (!list.some((member) => member.email === deal?.leadUser?.email)) {
        list.unshift({
          key: leadKey,
          name: deal?.leadUser?.fullName ?? deal?.leadUser?.email ?? "Deal lead",
          role: "Deal lead",
          email: deal?.leadUser?.email ?? "",
          avatarUrl: undefined,
        });
      }
    }

    return list;
  }, [deal?.leadUser, deal?.usersInvolved]);

  const headerProps: DealSummaryHeaderSectionProps = {
    title: deal?.name ?? "",
    isLoading,
    pipelineTypeLabel,
    statusLabel: deal?.status ?? "",
    stageLabel: deal?.stage ? stringKeyToSentenceCase(deal.stage) : "",
    stageItems,
    tags,
    onTagsClick: () => setShowTagsPanel(true),
    kpiCards,
    activeTab,
    onTabChange: (value) => goToTab(value as DealTab),
    onBack: handleBackClick,
    quickActionsMenuOptionsList: dealQuickActionsMenuOptionsList,
    showQuickActionsMenu,
    setShowQuickActionsMenu,
  };

  return (
    <div className="DealSummaryPage">
      <SeedNavigation activeElement="Portfolio" />
      <div className="DealSummaryContainer">
        {/* // eslint-disable-next-line react/jsx-props-no-spreading */}
        <DealSummaryHeaderSection
          title={headerProps.title}
          isLoading={headerProps.isLoading}
          pipelineTypeLabel={headerProps.pipelineTypeLabel}
          statusLabel={headerProps.statusLabel}
          stageLabel={headerProps.stageLabel}
          stageItems={headerProps.stageItems}
          tags={headerProps.tags}
          onTagsClick={headerProps.onTagsClick}
          kpiCards={headerProps.kpiCards}
          activeTab={headerProps.activeTab}
          onTabChange={headerProps.onTabChange}
          onBack={headerProps.onBack}
          quickActionsMenuOptionsList={headerProps.quickActionsMenuOptionsList}
          showQuickActionsMenu={headerProps.showQuickActionsMenu}
          setShowQuickActionsMenu={headerProps.setShowQuickActionsMenu}
        />
        {deal && !canEdit && (
          <InformationBox variant="info" corners="rounded">
            {`This deal has been ${deal.status?.toLowerCase()} and can no longer be edited.`}
          </InformationBox>
        )}
        {activeTab === "Overview" && (
          <DealSummary
            deal={deal}
            canEdit={canEdit}
            isEditing={isEditing}
            isSaving={isSaving}
            validationErrors={validationErrors}
            dealSummaryForm={dealSummaryForm}
            dealSourceOptions={dealSourceOptions}
            organisationUsersSelectData={organisationUsersSelectData}
            notePreviewItems={notePreviewItems}
            startEditing={startEditing}
            cancelEditing={cancelEditing}
            saveDealSummary={saveDealSummary}
            setDealSummaryFormValue={setDealSummaryFormValue}
            dealStructureColumns={dealStructureColumns}
            dealStructureData={dealStructureData}
            onChangeDealStructure={onChangeDealStructure}
            workflowUuid={dealWorkflowUuid}
            teamMembers={teamMembers}
            onViewNotes={() => setShowNotesPanel(true)}
            onViewDealStructure={() => goToTab("Deal structure")}
            onApplyWorkflow={() => setShowSelectWorkflowTemplateModal(true)}
          />
        )}

        {activeTab === "Deal structure" && (
          <DealStructure deal={deal} canEdit={canEdit} onDealStructureSaved={refreshDeal} />
        )}

        {activeTab === "Due diligence" && (
          <DealDiligence
            canUploadDocument={canUploadDealDocument}
            dealDocumentsColumns={dealDocumentsColumns}
            dealDocumentsLoading={dealDocumentsLoading}
            onDealDocGridChange={onDealDocGridChange}
            onUploadDocument={() => openUploadDealDocumentModal()}
            dealDocumentsRefreshToken={dealDocumentsRefreshToken}
          />
        )}

        <SeedFooter />
        <TagsPanel
          objectType={InteractionObjectType.DEAL}
          objectUuid={deal?.uuid}
          show={showTagsPanel}
          onClose={() => setShowTagsPanel(false)}
          shouldRefreshTags={refreshTags}
        />
        {deal?.uuid && (
          <NotesPanel
            objectUuid={deal.uuid}
            objectType={InteractionObjectType.DEAL}
            show={showNotesPanel}
            onClose={() => {
              setShowNotesPanel(false);
              refreshNotes();
            }}
          />
        )}
        {dealUuid && (
          <UploadDealDocumentModal
            show={showUploadDealDocumentModal}
            closeModal={closeUploadDealDocumentModal}
            dealUuid={dealUuid}
            dealDocumentUuid={uploadDealDocumentModalDealDocumentUuid}
            documentType={uploadDealDocumentModalDocumentType}
            customVariant={uploadDealDocumentModalCustomVariant}
            refreshDocuments={dealDocumentsRefreshToken}
            setRefreshDocuments={setDealDocumentsRefreshToken}
          />
        )}
        {deal && (
          <CloseDealModal
            show={showCloseDealModal}
            closeModal={closeCloseDealModal}
            deal={deal}
            dealUuid={dealUuid}
            workflowUuid={dealWorkflowUuid}
            refreshDeal={refreshDeal}
            applyCloseDealUpdate={applyCloseDealUpdate}
          />
        )}
        {deal && (
          <EditDealDatesPanel
            deal={deal}
            workflowTemplatePhases={workflowTemplatePhases}
            show={showEditDealDatesPanel}
            onClose={() => setShowEditDealDatesPanel(false)}
            onSaved={() => {
              setShowEditDealDatesPanel(false);
              refreshDeal();
            }}
          />
        )}
        {dealUuid && (
          <SelectWorkflowTemplateModal
            show={showSelectWorkflowTemplateModal}
            closeModal={() => setShowSelectWorkflowTemplateModal(false)}
            dealUuid={dealUuid}
            workflowType="Deal"
          />
        )}
        <SelectDealKpisPanel
          show={showSelectDealKpisPanel}
          selectedKpiKeys={selectedKpiKeys}
          onClose={() => setShowSelectDealKpisPanel(false)}
          onSave={handleSaveDealKpis}
        />
      </div>
    </div>
  );
};

export default Deal;
